import { useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CockpitPageShell } from "@/components/cockpit/CockpitPageShell";
import { CockpitErrorNotice } from "@/components/cockpit/CockpitErrorNotice";
import { CockpitSkeletonBlock } from "@/components/cockpit/CockpitSkeleton";
import { listCockpitFaq } from "@/lib/cockpit-faq.functions";

export const Route = createFileRoute("/_cockpit/cockpit/hilfe")({
  head: () => ({
    meta: [
      { title: "Hilfe & FAQ – Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CockpitFaqPage,
});

function CockpitFaqPage() {
  const listFn = useServerFn(listCockpitFaq);
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit", "faq"],
    queryFn: () => listFn(),
    staleTime: 5 * 60_000,
  });

  const denied = data ? data.allowed === false : false;

  useEffect(() => {
    if (denied) navigate({ to: "/cockpit", replace: true });
  }, [denied, navigate]);

  if (isLoading) {
    return (
      <CockpitPageShell width="reading">
        <CockpitSkeletonBlock />
      </CockpitPageShell>
    );
  }

  if (error) {
    return (
      <CockpitPageShell width="reading">
        <CockpitErrorNotice error={error} title="Hilfe-Inhalte konnten nicht geladen werden" />
      </CockpitPageShell>
    );
  }

  if (denied || !data || data.groups.length === 0) {
    return null;
  }

  return (
    <CockpitPageShell width="reading">
      <header className="mb-8 md:mb-12">
        <div className="flex items-center gap-2 text-primary">
          <HelpCircle className="h-5 w-5" aria-hidden />
          <span className="font-display text-xs font-extrabold uppercase tracking-wide">
            Hilfe
          </span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold text-primary md:text-4xl">
          Hilfe &amp; FAQ
        </h1>
        <p className="mt-3 font-body text-sm leading-relaxed text-muted-foreground md:text-base">
          Antworten auf die häufigsten Fragen zur Zusammenarbeit mit creategy und zur Nutzung
          Ihres Kampagnencockpits.
        </p>
      </header>

      <div className="space-y-10 md:space-y-14">
        {data.groups.map((group) => (
          <section key={group.id} aria-labelledby={`faq-${group.slug}`}>
            <h2
              id={`faq-${group.slug}`}
              className="font-display text-xl font-extrabold text-primary md:text-2xl"
            >
              {group.title}
            </h2>
            <Accordion type="single" collapsible className="mt-3 w-full">
              {group.items.map((item) => (
                <AccordionItem key={item.id} value={item.id} className="border-foreground/15">
                  <AccordionTrigger className="py-5 text-left font-display text-base font-extrabold text-primary hover:no-underline md:text-lg [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-primary">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 font-body text-sm leading-relaxed text-muted-foreground md:text-base">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}
      </div>

      <div className="mt-12 rounded-lg border border-border bg-secondary/40 p-5 md:p-6">
        <p className="font-body text-sm leading-relaxed text-foreground md:text-base">
          Ihre Frage war nicht dabei? Ihr Ansprechpartner hilft Ihnen gern weiter.
        </p>
        <Link
          to="/cockpit"
          hash="ansprechpartner"
          className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-brand-button px-4 py-2.5 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-auto"
        >
          Zu Ihrem Ansprechpartner
        </Link>
      </div>
    </CockpitPageShell>
  );
}
